import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { useNavigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Bell,
  BellRing,
  Check,
  CheckCheck,
  ChevronRight,
  Clock,
  MapPin,
  AlertTriangle,
  Shield,
  FileWarning,
  Settings,
  Volume2,
  VolumeX,
  X,
  Loader2,
  Calendar,
  Route,
} from 'lucide-react';

// Notification type icons and colors
const NOTIFICATION_CONFIG = {
  SHIFT_ASSIGNED: { icon: Calendar, color: 'text-blue-400', bg: 'bg-blue-500/20' },
  SHIFT_UPDATED: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/20' },
  SHIFT_CANCELLED: { icon: X, color: 'text-red-400', bg: 'bg-red-500/20' },
  SHIFT_REMINDER_24H: { icon: Bell, color: 'text-blue-400', bg: 'bg-blue-500/20' },
  SHIFT_REMINDER_2H: { icon: BellRing, color: 'text-amber-400', bg: 'bg-amber-500/20' },
  SHIFT_REMINDER_30M: { icon: BellRing, color: 'text-red-400', bg: 'bg-red-500/20' },
  SHIFT_ACCEPTANCE_REQUIRED: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/20' },
  PATROL_ASSIGNED: { icon: Route, color: 'text-cyan-400', bg: 'bg-cyan-500/20' },
  PATROL_READY: { icon: Route, color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
  MISSED_CHECKPOINT: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/20' },
  LICENCE_EXPIRY_30D: { icon: Shield, color: 'text-amber-400', bg: 'bg-amber-500/20' },
  LICENCE_EXPIRY_7D: { icon: Shield, color: 'text-orange-400', bg: 'bg-orange-500/20' },
  LICENCE_EXPIRED: { icon: Shield, color: 'text-red-400', bg: 'bg-red-500/20' },
  LICENCE_EXPIRING_ALERT: { icon: Shield, color: 'text-amber-400', bg: 'bg-amber-500/20' },
  LICENCE_EXPIRED_ALERT: { icon: Shield, color: 'text-red-400', bg: 'bg-red-500/20' },
  CRITICAL_INCIDENT: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/20' },
  INCIDENT_CREATED: { icon: FileWarning, color: 'text-amber-400', bg: 'bg-amber-500/20' },
  GEOFENCE_FAILURE: { icon: MapPin, color: 'text-red-400', bg: 'bg-red-500/20' },
  OFFICER_LATE_START: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/20' },
  OFFICER_NO_SHOW: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/20' },
  DEFAULT: { icon: Bell, color: 'text-zinc-400', bg: 'bg-zinc-500/20' },
};

// Notification sound — synth beep via Web Audio API.
// No notification.mp3 asset required.
const playNotificationBeep = () => {
  if (typeof window === 'undefined' || (!window.AudioContext && !window.webkitAudioContext)) return;

  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();

    const gain = ctx.createGain();
    gain.gain.value = 0.0001;
    gain.connect(ctx.destination);

    const tones = [
      { f: 660, t: 0 },
      { f: 880, t: 0.16 },
    ];

    tones.forEach(({ f, t }) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = f;

      const og = ctx.createGain();
      og.gain.setValueAtTime(0.0001, ctx.currentTime + t);
      og.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + t + 0.02);
      og.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.18);

      osc.connect(og);
      og.connect(gain);

      osc.start(ctx.currentTime + t);
      osc.stop(ctx.currentTime + t + 0.2);
    });

    setTimeout(() => ctx.close(), 800);
  } catch (e) {
    // Browser may block until first user gesture.
  }
};

export function NotificationBell() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] = useState(null);
  const dropdownRef = useRef(null);
  const prevUnreadCount = useRef(0);
  const preferencesRef = useRef(null);

  useEffect(() => {
    preferencesRef.current = preferences;
  }, [preferences]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!api) return;
    
    try {
      const response = await api.get('/notifications?limit=20');
      setNotifications(response.data.items || response.data.notifications || []);
      const newUnreadCount = response.data.unread_count || 0;
      
      // Play sound if new notification arrived
      if (newUnreadCount > prevUnreadCount.current && preferencesRef.current?.sound_enabled) {
        playNotificationBeep();
      }
      
      prevUnreadCount.current = newUnreadCount;
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [api]);

  // Fetch unread count only (for polling)
  const fetchUnreadCount = useCallback(async () => {
    if (!api) return;
    
    try {
      const response = await api.get('/notifications/unread-count');
      const newUnreadCount = response.data.unread_count || 0;
      
      // Play sound if new notification arrived
      if (newUnreadCount > prevUnreadCount.current && preferencesRef.current?.sound_enabled) {
        playNotificationBeep();
      }
      
      prevUnreadCount.current = newUnreadCount;
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [api]);

  // Fetch preferences
  const fetchPreferences = useCallback(async () => {
    if (!api) return;
    
    try {
      const response = await api.get('/notifications/preferences');
      setPreferences(response.data);
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
      // Default preferences
      setPreferences({
        push_enabled: true,
        sound_enabled: true,
        mute_all: false,
      });
    }
  }, [api]);

  // Initial fetch
  useEffect(() => {
    fetchPreferences();
    fetchUnreadCount();
  }, [fetchPreferences, fetchUnreadCount]);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchUnreadCount, 120000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    setLoading(true);
    try {
      await api.post('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    } finally {
      setLoading(false);
    }
  };

  // Toggle sound
  const toggleSound = async () => {
    try {
      const newValue = !preferences?.sound_enabled;
      await api.put('/notifications/preferences', { sound_enabled: newValue });
      setPreferences(prev => ({ ...prev, sound_enabled: newValue }));
      toast.success(newValue ? 'Sound enabled' : 'Sound muted');
    } catch (error) {
      toast.error('Failed to update preferences');
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    // Navigate based on entity type
    const actionUrl = notification.action_url;
    if (actionUrl) {
      setIsOpen(false);
      navigate(actionUrl);
    } else if (notification.entity_type === 'shift') {
      setIsOpen(false);
      navigate(`/shifts?id=${notification.entity_id}`);
    } else if (notification.entity_type === 'incident') {
      setIsOpen(false);
      navigate(`/incidents/${notification.entity_id}`);
    } else if (notification.entity_type === 'user') {
      setIsOpen(false);
      navigate(`/users/${notification.entity_id}`);
    }
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  // Get config for notification type
  const getConfig = (type) => NOTIFICATION_CONFIG[type] || NOTIFICATION_CONFIG.DEFAULT;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => {
          // First user tap unlocks Web Audio on iOS Safari.
          if (!isOpen && preferences?.sound_enabled) {
            try { playNotificationBeep(); } catch (_) {}
          }
          setIsOpen(!isOpen);
        }}
        className="relative"
        data-testid="notification-bell"
      >
        {unreadCount > 0 ? (
          <BellRing className="w-5 h-5 text-amber-400 animate-pulse" />
        ) : (
          <Bell className="w-5 h-5 text-zinc-400" />
        )}
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span 
            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center"
            data-testid="unread-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-[#18181b] border border-[#3f3f46] rounded-lg shadow-xl z-50 overflow-hidden animate-in slide-in-from-top-2">
          {/* Header */}
          <div className="px-4 py-3 border-b border-[#3f3f46] flex items-center justify-between">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <Bell className="w-4 h-4 text-cyan-400" />
              Notifications
            </h3>
            <div className="flex items-center gap-2">
              {/* Sound Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSound}
                className="h-8 w-8"
                title={preferences?.sound_enabled ? 'Mute sounds' : 'Enable sounds'}
              >
                {preferences?.sound_enabled ? (
                  <Volume2 className="w-4 h-4 text-cyan-400" />
                ) : (
                  <VolumeX className="w-4 h-4 text-zinc-500" />
                )}
              </Button>
              
              {/* Mark All Read */}
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  disabled={loading}
                  className="text-xs text-cyan-400 hover:text-cyan-300"
                >
                  {loading ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <CheckCheck className="w-3 h-3 mr-1" />
                  )}
                  Mark all read
                </Button>
              )}
            </div>
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-zinc-500">
                <Bell className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const config = getConfig(notification.type);
                const Icon = config.icon;

                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-4 py-3 border-b border-[#3f3f46] hover:bg-[#27272a] cursor-pointer transition-colors ${
                      !notification.is_read ? 'bg-cyan-500/5' : ''
                    }`}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-5 h-5 ${config.color}`} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium ${notification.is_read ? 'text-zinc-400' : 'text-white'}`}>
                            {notification.title}
                          </p>
                          {!notification.is_read && (
                            <span className="w-2 h-2 bg-cyan-400 rounded-full flex-shrink-0 mt-1" />
                          )}
                        </div>
                        <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-zinc-600 mt-1">
                          {formatTimeAgo(notification.created_at)}
                        </p>
                      </div>

                      {/* Chevron */}
                      <ChevronRight className="w-4 h-4 text-zinc-600 flex-shrink-0 self-center" />
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-[#3f3f46]">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsOpen(false);
                navigate('/notifications');
              }}
              className="w-full text-xs text-zinc-400 hover:text-white"
            >
              View all notifications
              <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
